import React from 'react';
import './EvidenceSourcesPanel.css';

type SourceCard = {
  id: string;
  label_en?: string;
  label_es?: string;
  blurb_en?: string;
  blurb_es?: string;
  status?: string;
  kind?: string;
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
};

const statusLabel = (status: string, es: boolean): string => {
  if (status === 'connected') return es ? 'Listo' : 'Ready';
  if (status === 'needs_connect') return es ? 'Falta conectar' : 'Needs setup';
  if (status === 'error') return es ? 'Error' : 'Error';
  return status || (es ? 'Apagado' : 'Off');
};

const nextStepFor = (row: SourceCard, es: boolean): string => {
  const status = String(row.status || 'off');
  const kind = String(row.kind || '');
  if (status === 'connected') {
    return es
      ? 'Disponible para buscar prueba en un paso del checklist.'
      : 'Available when a checklist step searches for proof.';
  }
  if (kind === 'oauth_mail' || kind === 'oauth_drive') {
    return es
      ? 'Conexión cloud aún no configurada en este workspace. Use Este Mac / Dropbox mientras tanto.'
      : 'Cloud link is not set up for this workspace yet. Use This Mac / Dropbox for now.';
  }
  if (kind === 'local_cloud') {
    return es
      ? 'Instale el agente en este Mac y asegúrese de que la carpeta CloudStorage exista.'
      : 'Install the agent on this Mac and make sure the CloudStorage folder exists.';
  }
  return es
    ? 'Instale el agente local para marcar este origen como listo.'
    : 'Install the local agent so this source can show as ready.';
};

/**
 * Proof Sources — connect-only catalog (Email, Drive, Dropbox, OneDrive, This Mac).
 * Search / smart scan / drafts live on the task strip, not here.
 */
export default function EvidenceSourcesPanel({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
}: Props): React.ReactElement {
  const es = language === 'es';
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const [sources, setSources] = React.useState<SourceCard[]>([]);
  const [privacy, setPrivacy] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!accessToken || !gateway) return;
    setBusy(true);
    try {
      const response = await fetch(`${gateway}/api/v1/evidence/sources`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = await response.json().catch(() => ({})) as {
        sources?: SourceCard[];
        privacy_note?: string;
        detail?: string;
      };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : (es ? 'No se pudo cargar Fuentes.' : 'Could not load Sources.'));
        return;
      }
      setSources(Array.isArray(body.sources) ? body.sources : []);
      setPrivacy(String(body.privacy_note || ''));
      setError('');
    } catch {
      setError(es ? 'No se pudo cargar Fuentes.' : 'Could not load Sources.');
    } finally {
      setBusy(false);
    }
  }, [accessToken, es, gateway]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const readyCount = sources.filter((row) => row.status === 'connected').length;
  const needCount = sources.length - readyCount;

  return (
    <section className="evidence-sources" aria-labelledby="evidence-sources-title" aria-busy={busy}>
      <header className="evidence-sources__head">
        <div>
          <p className="evidence-sources__kicker">{es ? 'Prueba' : 'Proof'}</p>
          <h4 id="evidence-sources-title">{es ? 'Dónde buscar prueba' : 'Where proof can be found'}</h4>
        </div>
        <p className="evidence-sources__lead">
          {es
            ? 'Esto solo conecta lugares. La búsqueda, el escaneo inteligente y los borradores están en la franja del agente de la tarea — no aquí.'
            : 'This only connects places. Search, smart scan, and drafts live on the task agent strip — not here.'}
        </p>
        {privacy ? <p className="evidence-sources__privacy">{privacy}</p> : null}
      </header>

      <ol className="evidence-sources__howto">
        <li>
          {es
            ? 'Revise qué orígenes están Listos (verde).'
            : 'Check which sources show Ready (green).'}
        </li>
        <li>
          {es
            ? 'Si dice Falta conectar, siga el paso bajo la tarjeta (agente local o cloud).'
            : 'If it says Needs setup, follow the step under the card (local agent or cloud).'}
        </li>
        <li>
          {es
            ? 'Vuelva al paso del checklist y use Do it for me / Aceptar prueba allí.'
            : 'Go back to the checklist step and use Do it for me / Accept proof there.'}
        </li>
      </ol>

      {busy ? (
        <p className="evidence-sources__note is-busy" role="status">
          <span className="evidence-sources__spinner" aria-hidden="true" />
          {es ? 'Cargando orígenes…' : 'Loading sources…'}
        </p>
      ) : (
        <p className="evidence-sources__summary" role="status">
          {es
            ? `${readyCount} listo(s)${needCount ? ` · ${needCount} por configurar` : ''}.`
            : `${readyCount} ready${needCount ? ` · ${needCount} still need setup` : ''}.`}
        </p>
      )}

      {error ? <p className="evidence-sources__error" role="alert">{error}</p> : null}

      <ul className="evidence-sources__grid" role="list">
        {sources.map((row) => {
          const id = String(row.id || '');
          const label = es ? (row.label_es || row.label_en || id) : (row.label_en || row.label_es || id);
          const blurb = es ? (row.blurb_es || row.blurb_en || '') : (row.blurb_en || row.blurb_es || '');
          const status = String(row.status || 'off');
          const ready = status === 'connected';
          return (
            <li key={id}>
              <article
                className={`evidence-sources__card${ready ? ' is-connected' : ' is-needs'}`}
                aria-label={`${label}: ${statusLabel(status, es)}`}
              >
                <span className="evidence-sources__card-top">
                  <strong>{label}</strong>
                  <em className={`evidence-sources__chip is-${status}`}>{statusLabel(status, es)}</em>
                </span>
                <span className="evidence-sources__blurb">{blurb}</span>
                <span className="evidence-sources__next">
                  <span className="evidence-sources__next-label">{es ? 'Qué hacer' : 'What to do'}</span>
                  {nextStepFor(row, es)}
                </span>
                {!ready && (String(row.kind || '').startsWith('local') || row.kind === 'local_folder' || row.kind === 'local_cloud') ? (
                  <a className="dashboard-btn evidence-sources__action" href="/console/agent">
                    {es ? 'Instalar / dispositivos' : 'Install / devices'}
                  </a>
                ) : null}
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
